import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, Filter, Search, Download, Eye, Edit, Trash2,
  Clock, CheckCircle2, AlertTriangle, X, Calendar,
  User, Building2, DollarSign, Package, ChevronRight,
  FileText, Send, Users, Tag, Star, Phone, Mail,
  TrendingUp, TrendingDown, Award, FileCheck, Receipt,
  Truck, Route, ArrowLeftRight, MapPin, Timer, 
  CheckSquare, XCircle, RefreshCw, Navigation,
  Box, BarChart3, AlertCircle, Move, Settings
} from 'lucide-react';

const WarehouseInventoryTab = ({ timeRange, region }) => {
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const subTabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3, description: 'Inventory summary & alerts' },
    { id: 'stock', label: 'Stock Management', icon: Box, description: 'Real-time inventory tracking' },
    { id: 'adjustments', label: 'Adjustments', icon: Move, description: 'Stock adjustments & transfers' },
    { id: 'cycle-counts', label: 'Cycle Counts', icon: CheckSquare, description: 'Inventory counts & audits' }
  ];

  // Mock data for inventory overview
  const inventoryStats = [
    {
      label: 'Total SKUs',
      value: '2,456',
      change: '+12',
      trend: 'up',
      color: 'blue'
    },
    {
      label: 'Stock Value',
      value: 'SAR 4.2M',
      change: '+8.5%',
      trend: 'up',
      color: 'green'
    },
    {
      label: 'Low Stock Items',
      value: '45',
      change: '-5',
      trend: 'down',
      color: 'red'
    },
    {
      label: 'Out of Stock',
      value: '12',
      change: '+2',
      trend: 'up',
      color: 'orange'
    }
  ];

  // Mock data for stock items
  const stockItems = [
    {
      id: 'SKU-001',
      name: 'Kitchen Knife Set',
      category: 'Kitchen Equipment',
      currentStock: 125,
      minStock: 50,
      maxStock: 200,
      unitCost: 450,
      location: 'A1-B2-C3',
      supplier: 'Al-Rashid Trading Co.',
      lastUpdated: '2024-03-15',
      status: 'in-stock'
    },
    {
      id: 'SKU-002',
      name: 'Commercial Oven',
      category: 'Kitchen Equipment',
      currentStock: 8,
      minStock: 10,
      maxStock: 25,
      unitCost: 8500,
      location: 'D4-E5-F6',
      supplier: 'Kitchen Pro Supplies',
      lastUpdated: '2024-03-14',
      status: 'low-stock'
    },
    {
      id: 'SKU-003',
      name: 'Refrigeration Unit',
      category: 'Kitchen Equipment',
      currentStock: 0,
      minStock: 5,
      maxStock: 15,
      unitCost: 12500,
      location: 'G7-H8-I9',
      supplier: 'Cold Storage Solutions',
      lastUpdated: '2024-03-13',
      status: 'out-of-stock'
    }
  ];

  // Mock data for recent adjustments
  const adjustments = [
    {
      id: 'ADJ-2024-001',
      type: 'receipt',
      item: 'Kitchen Knife Set',
      quantity: 50,
      reason: 'Purchase Order Receipt',
      user: 'Ahmed Al-Rashid',
      date: '2024-03-15',
      reference: 'PO-2024-001'
    },
    {
      id: 'ADJ-2024-002',
      type: 'transfer',
      item: 'Commercial Oven',
      quantity: -2,
      reason: 'Transfer to Store #123',
      user: 'Sarah Johnson',
      date: '2024-03-14',
      reference: 'TRF-2024-001'
    },
    {
      id: 'ADJ-2024-003',
      type: 'adjustment',
      item: 'Refrigeration Unit',
      quantity: -1,
      reason: 'Damage Adjustment',
      user: 'Mohammed Ali',
      date: '2024-03-13',
      reference: 'DAM-2024-001'
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      'in-stock': 'bg-green-100 text-green-800',
      'low-stock': 'bg-yellow-100 text-yellow-800',
      'out-of-stock': 'bg-red-100 text-red-800',
      'receipt': 'bg-green-100 text-green-800',
      'transfer': 'bg-blue-100 text-blue-800',
      'adjustment': 'bg-purple-100 text-purple-800'
    };
    return colors[status] || colors['in-stock'];
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'receipt':
        return <CheckCircle2 className="w-4 h-4" />;
      case 'transfer':
        return <ArrowLeftRight className="w-4 h-4" />;
      case 'adjustment':
        return <Settings className="w-4 h-4" />;
      default:
        return <Package className="w-4 h-4" />;
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Inventory Overview</h3>
          <p className="text-sm text-gray-500 mt-1">Real-time inventory tracking and management</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
          <Plus className="w-4 h-4" />
          New Item
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {inventoryStats.map((stat, index) => (
          <motion.div
            key={index}
            whileHover={{ scale: 1.02 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-lg bg-${stat.color}-100`}>
                <Box className={`w-6 h-6 text-${stat.color}-600`} />
              </div>
            </div>
            <div className={`mt-4 flex items-center text-sm ${
              stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
            }`}>
              {stat.trend === 'up' ? (
                <TrendingUp className="w-4 h-4 mr-1" />
              ) : (
                <TrendingDown className="w-4 h-4 mr-1" />
              )}
              <span>{stat.change}</span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Alerts Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-gray-900">Stock Alerts</h4>
          <button className="text-sm text-emerald-600 hover:text-emerald-700">View All</button>
        </div>
        
        <div className="space-y-3">
          {stockItems.filter(item => item.status !== 'in-stock').map((item) => (
            <div key={item.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center space-x-3">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{item.name}</p>
                  <p className="text-xs text-gray-500">Current: {item.currentStock} | Min: {item.minStock}</p>
                </div>
              </div>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                {item.status.replace('-', ' ')}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStockManagement = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Stock Management</h3>
          <p className="text-sm text-gray-500 mt-1">Real-time inventory tracking and stock levels</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
            <Plus className="w-4 h-4" />
            Add Item
          </button>
        </div>
      </div>

      {/* Stock Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Item Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stock Levels
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {stockItems.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{item.id}</p>
                      <p className="text-sm text-gray-900">{item.name}</p>
                      <p className="text-xs text-gray-500">{item.category}</p>
                      <p className="text-xs text-gray-500">Supplier: {item.supplier}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Current:</span>
                        <span className="text-sm font-medium">{item.currentStock}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Min:</span>
                        <span className="text-sm">{item.minStock}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Max:</span>
                        <span className="text-sm">{item.maxStock}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">{item.location}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                      {item.status.replace('-', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        SAR {(item.currentStock * item.unitCost).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">
                        SAR {item.unitCost} per unit
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors">
                        <Move className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderAdjustments = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Stock Adjustments</h3>
          <p className="text-sm text-gray-500 mt-1">Stock adjustments, put-away, and transfers</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
          <Plus className="w-4 h-4" />
          New Adjustment
        </button>
      </div>

      {/* Adjustments Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Adjustment Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reason
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {adjustments.map((adjustment) => (
                <tr key={adjustment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{adjustment.id}</p>
                      <p className="text-sm text-gray-900">{adjustment.item}</p>
                      <p className="text-xs text-gray-500">Ref: {adjustment.reference}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className={`p-1 rounded-lg ${getStatusColor(adjustment.type).split(' ')[0]}`}>
                        {getTypeIcon(adjustment.type)}
                      </div>
                      <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(adjustment.type)}`}>
                        {adjustment.type}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-sm font-medium ${
                      adjustment.quantity > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {adjustment.quantity > 0 ? '+' : ''}{adjustment.quantity}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">{adjustment.reason}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">{adjustment.user}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">
                      {new Date(adjustment.date).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderCycleCounts = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Cycle Counts</h3>
          <p className="text-sm text-gray-500 mt-1">Inventory counts and audits</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
          <Plus className="w-4 h-4" />
          New Count
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="text-center py-12">
          <CheckSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Cycle Counts</h3>
          <p className="text-gray-500 mb-6">Create a new cycle count to start inventory auditing</p>
          <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
            Start Cycle Count
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex space-x-1">
          {subTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeSubTab === tab.id
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title={tab.description}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content based on active sub-tab */}
      {activeSubTab === 'overview' && renderOverview()}
      {activeSubTab === 'stock' && renderStockManagement()}
      {activeSubTab === 'adjustments' && renderAdjustments()}
      {activeSubTab === 'cycle-counts' && renderCycleCounts()}
    </div>
  );
};

export default WarehouseInventoryTab; 